import axios from 'axios';
import { chunk } from 'lodash';
import {
  LevelCompletedEvent,
  LevelPlayEvent,
  LevelPlayingEvent,
  RepertoirePlayingEvent,
  TierCompletedEvent,
  TierStartedEvent,
  LevelStartedEvent,
  EventTypes,
  EventType,
  ExercisePlaythroughCompletedEvent,
} from 'Types/EventTypes';
import logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { getCurrentAuthToken } from 'Utils/Auth';
import { getCurrentAppVersion } from 'Utils/appVersion';
import { MAX_RESENDS, isDeviceOffline, isPermanentReject, resendDelayMs } from './eventSendPolicy';
import { claimKeepaliveChars, keepaliveCharsAvailable } from './keepaliveBudget';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const eventMaxBatchSize = process.env.REACT_APP_EVENT_MAX_BATCH_SIZE;
if (!eventMaxBatchSize) {
  throw Error('REACT_APP_EVENT_MAX_BATCH_SIZE is not set');
}

const eventMaxBatchSizeInt = parseInt(eventMaxBatchSize);

type BaseEventType = {
  datetime: string;
  analytics_session_id: string;
  // MUS-418: per-event idempotency key (uuid v4), stamped at enqueue in the saga.
  client_event_id?: string;
};

type PlaySessionEventType = BaseEventType & { play_session_id?: string };

type PlaySessionEvents = PlaySessionEventType &
  (
    | LevelCompletedEvent
    | LevelPlayEvent
    | LevelPlayingEvent
    | RepertoirePlayingEvent
    | TierCompletedEvent
    | TierStartedEvent
    | LevelStartedEvent
    | ExercisePlaythroughCompletedEvent
  );

type GenericEventType = (BaseEventType & EventTypes) | PlaySessionEvents;

// MUS-414: the /events batch handler no longer 422s a whole batch on a bad event.
// It returns a per-event partial-success summary. unknown / duplicate / invalid ride
// a 200; only a genuine server persist fault (failed[]) still yields a 422 (carrying
// this same body). MUS-378 reads this shape to tell a real client-error from a benign
// skip. Fields are optional because we parse a response we did not author.
type EventsBatchSummary = {
  persisted?: number;
  skipped?: {
    unknown?: number;
    duplicate?: number;
    invalid?: Array<{ event_type?: string; reason?: unknown }>;
  };
  failed?: Array<{ event_type?: string; reason?: unknown }>;
};
// The most events kept for a resend. A send that fails with no response while
// offline does not count toward MAX_RESENDS, so this bounds what an hour or so
// offline can hold. Past it the oldest are dropped and reported once until a send
// is accepted again.
const MAX_RETAINED_EVENTS = 2000;

// A page being torn down may have about 64 KB of keepalive requests in flight in
// total, and the play-time note flush sends its own on the same signals. The queue
// keeps its teardown send under this share, or under what the play-time notes have
// left of the allowance if that is less (see keepaliveBudget), and leaves the rest
// pending.
const KEEPALIVE_BUDGET_CHARS = 40 * 1024;

// The server stores each app sitting's app version from this header, and ends the
// sittings a batch names when the batch carries the session-end header (the
// client's last send before a sign-out).
const APP_VERSION_HEADER = 'x-museflow-app-version';
const SESSION_END_HEADER = 'x-museflow-session-end';
const SIGNED_OUT = 'signed_out';

type BatchHeaders = Record<string, string>;

// The app version, read once in the background (from the first queued event) so
// that a send never waits for it: a send goes out without the header until it is
// known, and the server keeps the first version a sitting reports.
let knownAppVersion: string | null = null;
let appVersionRequested = false;

function requestAppVersion(): void {
  if (appVersionRequested) return;
  appVersionRequested = true;
  getCurrentAppVersion()
    .then((appVersion) => {
      knownAppVersion = appVersion;
    })
    .catch(() => {});
}

function appVersionHeaders(): BatchHeaders {
  requestAppVersion();
  return knownAppVersion ? { [APP_VERSION_HEADER]: knownAppVersion } : {};
}

// One event waiting to be sent or resent: a stable id (the queue's own, since not
// every payload carries a client_event_id), how many of its sends have failed (this
// sets the gap), how many of those count toward MAX_RESENDS (not the ones that
// failed offline), and the earliest time (epoch ms) it may be sent again (0 for an
// event not sent yet).
type PendingEvent = {
  id: number;
  event: GenericEventType;
  failedSends: number;
  countedResends: number;
  nextSendAt: number;
};

// Real user pauses/repeats are spaced much further apart; double-fires from
// effect closures, paired listeners, or upstream Phaser duplicates almost
// always land within a few hundred ms of each other.
const DEDUPE_WINDOW_MS = 1000;

// Fields stripped from the dedup signature so events with otherwise-identical
// payloads are recognized as duplicates. `datetime` differs by definition;
// the others are session-scoped and identical across consecutive emits anyway.
// `client_event_id` (MUS-418) is a fresh uuid per enqueue, so it MUST be ignored
// here - otherwise every event would have a unique signature and this near-duplicate
// window dedup would never collapse anything. (The backend uses client_event_id for
// its own retry dedup; the two mechanisms are independent.)
const DEDUPE_IGNORED_FIELDS = new Set([
  'datetime',
  'analytics_session_id',
  'platform',
  'client_event_id',
]);

function buildDedupeSignature(event: GenericEventType): string {
  const keyFields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event)) {
    if (!DEDUPE_IGNORED_FIELDS.has(key)) {
      keyFields[key] = value;
    }
  }
  return JSON.stringify(keyFields);
}

// Groups events, in order, into keepalive requests that stay within budget
// characters of JSON in total, each holding at most maxPerRequest events. Stops at
// the first event that does not fit, so the oldest go first. A request's body is
// its events' JSON, a comma between each two, and the array's two brackets.
function keepaliveRequests(
  pending: PendingEvent[],
  budget: number,
  maxPerRequest: number,
): PendingEvent[][] {
  const requests: PendingEvent[][] = [];
  let remaining = budget;
  let current: PendingEvent[] = [];
  for (const entry of pending) {
    const startsRequest = current.length === 0 || current.length >= maxPerRequest;
    const size = JSON.stringify(entry.event).length + (startsRequest ? 2 : 1);
    if (size > remaining) break;
    if (current.length >= maxPerRequest) {
      requests.push(current);
      current = [];
    }
    current = [...current, entry];
    remaining -= size;
  }
  return current.length ? [...requests, current] : requests;
}

// Every event is kept until the server accepts it, rejects it for good (a
// permanent 4xx: dropped and reported, as before), or fails MAX_RESENDS resends.
// A send that failed for a reason a resend can fix (no response, a 5xx, a
// retryable 4xx) is retried after a growing gap, with the same payloads, so the
// server deduplicates on client_event_id any that did land.
class EventQueue {
  queue: GenericEventType[] = [];
  private pending: PendingEvent[] = [];
  private inFlight: Set<number> = new Set();
  // The events in a teardown keepalive that has not answered yet. A second teardown
  // signal (hidden, then pagehide) does not send them again.
  private inKeepalive: Set<number> = new Set();
  private nextPendingId = 1;
  private retainedLimitReported = false;
  // Bumped by discardPending (sign-out). A sign-out flush that outlives its wait
  // sends no further batch after it.
  private discardGeneration = 0;

  pushEvent(event: GenericEventType) {
    requestAppVersion();
    this.queue.push(event);
  }

  // Drop events whose (event_type + payload-minus-temporal-fields) signature
  // already appeared within DEDUPE_WINDOW_MS. Catches the client-side double-
  // emit pattern (MUS-232) without losing intentional repeated actions.
  filterRecentDuplicates(queue: GenericEventType[]): GenericEventType[] {
    const lastSeenAt = new Map<string, number>();
    const result: GenericEventType[] = [];
    for (const event of queue) {
      const signature = buildDedupeSignature(event);
      const eventTime = new Date(event.datetime).getTime();
      const previousTime = lastSeenAt.get(signature);
      if (previousTime !== undefined && eventTime - previousTime < DEDUPE_WINDOW_MS) {
        logger.debug('dropping duplicate event within dedupe window', {
          event_type: event.event_type,
          gap_ms: eventTime - previousTime,
        });
        continue;
      }
      lastSeenAt.set(signature, eventTime);
      result.push(event);
    }
    return result;
  }

  batchEvents(queue: GenericEventType[]): GenericEventType[][] {
    const batches: GenericEventType[][] = [[]];
    let indx = 0;
    while (queue.length > 0) {
      // logger.info("queue is larger than zero")
      // logger.info("queue.length", queue.length)
      if (batches[indx].length <= eventMaxBatchSizeInt) {
        batches[indx].push(queue.shift());
      } else {
        batches.push([queue.shift()]);
        indx += 1;
      }
    }
    return batches;
  }

  filterDuplicateEvents(queue: GenericEventType[]) {
    let latestLevelPlayingEvent: (LevelPlayingEvent & PlaySessionEventType) | undefined,
      latestTierCompletedEvent: (TierCompletedEvent & PlaySessionEventType) | undefined,
      latestLevelCompletedEvent: (LevelCompletedEvent & PlaySessionEventType) | undefined;

    const filteredQeue = queue.reduce(
      (accumulatedEvents: GenericEventType[], currentEvent: GenericEventType) => {
        switch (currentEvent.event_type) {
          case EventType.LevelCompletedEvent:
            if (
              !latestLevelCompletedEvent ||
              new Date(currentEvent.datetime) > new Date(latestLevelCompletedEvent.datetime)
            ) {
              latestLevelCompletedEvent = currentEvent;
            }
            break;
          case EventType.LevelPlayingEvent:
            if (!latestLevelPlayingEvent || currentEvent.play_time > currentEvent.play_time) {
              latestLevelPlayingEvent = currentEvent;
            }
            break;
          case EventType.TierCompletedEvent:
            if (
              !latestTierCompletedEvent ||
              new Date(currentEvent.datetime) > new Date(latestTierCompletedEvent.datetime)
            ) {
              latestTierCompletedEvent = currentEvent;
            }
            break;
          default:
            accumulatedEvents.push(currentEvent);
        }
        if (
          !latestLevelCompletedEvent &&
          currentEvent.event_type === EventType.LevelCompletedEvent
        ) {
          latestLevelCompletedEvent = currentEvent;
          accumulatedEvents.push(currentEvent);
        }
        return accumulatedEvents;
      },
      [],
    );

    for (const evt of [
      latestLevelCompletedEvent,
      latestLevelPlayingEvent,
      latestTierCompletedEvent,
    ]) {
      if (evt) {
        filteredQeue.push(evt);
      }
    }
    return filteredQeue;
  }

  // MUS-378: surface a partial-success summary's real client errors to RUM.
  // "Real" = an event the backend won't persist: skipped.invalid (a known type that
  // failed validation, e.g. MUS-377's rh_accuracy NOT-NULL) or failed (a server persist
  // fault). skipped.duplicate (retry dedup) and skipped.unknown (a type-registry gap
  // already logged server-side by MUS-414) are benign and intentionally NOT reported,
  // to keep RUM free of alarm noise. Emits at most one RUM error, and returns true when
  // it did, so the caller can avoid a second, redundant emit for the same failure.
  private reportRejectedEntries(
    summary: EventsBatchSummary | undefined,
    httpStatus: number | undefined,
  ): boolean {
    const invalid = summary?.skipped?.invalid ?? [];
    const failed = summary?.failed ?? [];
    if (invalid.length === 0 && failed.length === 0) {
      return false;
    }
    const detail = {
      httpStatus,
      invalidCount: invalid.length,
      failedCount: failed.length,
      invalidTypes: [...new Set(invalid.map((e) => e?.event_type ?? 'unknown'))],
      failedTypes: [...new Set(failed.map((e) => e?.event_type ?? 'unknown'))],
    };
    reportError(new Error(`events POST rejected entries ${JSON.stringify(detail)}`), 'events.post');
    return true;
  }

  // Drop the events of a finished send (accepted, or rejected for good) from pending.
  private acknowledge(ids: Set<number>) {
    this.pending = this.pending.filter((entry) => !ids.has(entry.id));
  }

  // After a failed send a resend could fix, each event in it counts one more failed
  // send, and one more toward MAX_RESENDS unless the send got no response while the
  // device is offline. An event past MAX_RESENDS is dropped and reported; the rest
  // wait out their backoff.
  private scheduleResends(ids: Set<number>, httpStatus: number | undefined) {
    const now = Date.now();
    const countsTowardLimit = !(httpStatus === undefined && isDeviceOffline());
    const updated = this.pending.map((entry) => {
      if (!ids.has(entry.id)) return entry;
      const failedSends = entry.failedSends + 1;
      return {
        ...entry,
        failedSends,
        countedResends: entry.countedResends + (countsTowardLimit ? 1 : 0),
        nextSendAt: now + resendDelayMs(failedSends),
      };
    });
    const exhausted = updated.filter(
      (entry) => ids.has(entry.id) && entry.countedResends > MAX_RESENDS,
    );
    this.pending = updated.filter((entry) => !exhausted.includes(entry));
    if (exhausted.length > 0) {
      const detail = {
        httpStatus,
        droppedEvents: exhausted.length,
        droppedTypes: [...new Set(exhausted.map((entry) => entry.event.event_type))],
        resends: MAX_RESENDS,
      };
      reportError(
        new Error(`events resend limit reached, events dropped ${JSON.stringify(detail)}`),
        'events.post',
      );
    }
  }

  // Keep at most MAX_RETAINED_EVENTS, dropping the oldest. Reported once (counts only)
  // until a send is accepted again.
  private dropOldestPastRetainedLimit() {
    const excess = this.pending.length - MAX_RETAINED_EVENTS;
    if (excess <= 0) return;
    const dropped = this.pending.slice(0, excess);
    this.pending = this.pending.slice(excess);
    if (this.retainedLimitReported) return;
    this.retainedLimitReported = true;
    const detail = { droppedEvents: dropped.length, limit: MAX_RETAINED_EVENTS };
    reportError(
      new Error(`events retained limit reached, oldest dropped ${JSON.stringify(detail)}`),
      'events.post',
    );
  }

  // Move the events queued since the last send into pending, after the
  // near-duplicate filters. The queue empties at once, so a concurrent call cannot
  // cut the same events twice.
  private cutQueue() {
    if (this.queue.length === 0) return;
    const deduped = this.filterDuplicateEvents(this.filterRecentDuplicates(this.queue));
    this.queue = [];
    const firstId = this.nextPendingId;
    this.nextPendingId += deduped.length;
    const cut = deduped.map((event, index) => ({
      id: firstId + index,
      event,
      failedSends: 0,
      countedResends: 0,
      nextSendAt: 0,
    }));
    this.pending = [...this.pending, ...cut];
    this.dropOldestPastRetainedLimit();
  }

  // MUS-378: the events POST used to be swallowed - the catch only logger.error'd and
  // the success body was discarded. Now one path handles both:
  //  - a 2xx can still carry per-event rejections in its body (MUS-414) -> report them;
  //  - a non-2xx (network error / 4xx / the 422 that carries failed[]) was previously
  //    invisible in production -> report it, then keep the existing log breadcrumb.
  // A permanent reject drops the batch; any other failure keeps it for a resend after
  // a backoff, and is reported on the first failed send only (the resend limit and the
  // retained limit report their own drops). Never throws, so one bad batch does not
  // abort the rest of the queue flush.
  private async postBatch(batch: PendingEvent[], extraHeaders: BatchHeaders = {}): Promise<void> {
    const ids = new Set(batch.map((entry) => entry.id));
    ids.forEach((id) => this.inFlight.add(id));
    try {
      const headers = { ...appVersionHeaders(), ...extraHeaders };
      const response = await axios.post<EventsBatchSummary>(
        baseUrl + 'events',
        batch.map((entry) => entry.event),
        { headers },
      );
      this.acknowledge(ids);
      this.retainedLimitReported = false;
      this.reportRejectedEntries(response?.data, response?.status);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const data = axios.isAxiosError(err)
        ? (err.response?.data as EventsBatchSummary | undefined)
        : undefined;
      const permanent = status !== undefined && isPermanentReject(status);
      const firstFailure = batch.some((entry) => entry.failedSends === 0);
      if (permanent) {
        this.acknowledge(ids);
      } else {
        this.scheduleResends(ids, status);
      }
      if (permanent || firstFailure) {
        // The 422 partial-success case carries failed[]/invalid[] in the error response;
        // that structured report is the informative one when present.
        const reportedEntries = this.reportRejectedEntries(data, status);
        // Otherwise (network error, or a 4xx/5xx with no summary body) surface the transport
        // failure itself so it is visible in RUM rather than only in the console.
        if (!reportedEntries) {
          reportError(err, 'events.post');
        }
      }
      logger.error('events batch error');
      logger.error(err);
    } finally {
      ids.forEach((id) => this.inFlight.delete(id));
    }
  }

  // Sends the queued events, and those waiting to be resent whose backoff has run
  // out, in batches of at most REACT_APP_EVENT_MAX_BATCH_SIZE, oldest first.
  async clearQueue() {
    this.cutQueue();
    const now = Date.now();
    const due = this.pending.filter(
      (entry) => !this.inFlight.has(entry.id) && entry.nextSendAt <= now,
    );
    if (due.length === 0) {
      logger.debug('no events to send');
      return;
    }
    const batches = chunk(due, eventMaxBatchSizeInt);
    logger.debug('sending batch of size ' + batches[0].length);
    await this.postBatch(batches[0]);
    for (const batch of batches.slice(1, batches.length)) {
      await new Promise((prom) => setTimeout(prom, 200));
      await this.postBatch(batch);
    }
    logger.debug('queue cleared');
  }

  // One keepalive request, whose events flushOnTeardown has already marked as in
  // flight and whose body it has already claimed. The events stay pending until it
  // answers: accepted (or rejected for good) drops them, any other failure schedules
  // a resend. When the page does not survive there is no answer, and nothing a later
  // page could have sent is lost.
  private async sendKeepalive(
    batch: PendingEvent[],
    body: string,
    token: string | undefined,
    versionHeaders: BatchHeaders,
    releaseClaim: () => void,
  ) {
    const ids = new Set(batch.map((entry) => entry.id));
    try {
      const response = await fetch(baseUrl + 'events', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...versionHeaders,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body,
      });
      if (response.ok) {
        this.acknowledge(ids);
        this.retainedLimitReported = false;
        const summary = (await response.json().catch(() => undefined)) as
          | EventsBatchSummary
          | undefined;
        this.reportRejectedEntries(summary, response.status);
      } else if (isPermanentReject(response.status)) {
        this.acknowledge(ids);
        reportError(
          new Error(
            `events keepalive POST rejected ${JSON.stringify({ httpStatus: response.status, events: batch.length })}`,
          ),
          'events.post',
        );
      } else {
        this.scheduleResends(ids, response.status);
      }
    } catch (err) {
      this.scheduleResends(ids, undefined);
      logger.warn('[events] keepalive send failed', err);
    } finally {
      releaseClaim();
      ids.forEach((id) => {
        this.inFlight.delete(id);
        this.inKeepalive.delete(id);
      });
    }
  }

  // Page hidden, app backgrounded or pagehide: the dispatch timer stops and the page
  // may not come back, so send what is pending (queued now, or waiting for a resend)
  // with keepalive fetch, which survives the page going away and carries the
  // Authorization header. Oldest first, within this queue's share of the keepalive
  // budget (less when the play-time notes, which send first, have claimed more than
  // the rest of the allowance); the rest stay pending for the next send if the page
  // survives.
  async flushOnTeardown(): Promise<void> {
    if (process.env.REACT_APP_SEND_EVENTS !== 'true') return;
    if (typeof fetch !== 'function') return;
    this.cutQueue();
    // Events whose normal POST is still in flight go too: that request dies with the
    // page, and the server deduplicates the copy if both land. Events already in a
    // teardown keepalive do not: that request survives the page.
    const budget = Math.min(KEEPALIVE_BUDGET_CHARS, keepaliveCharsAvailable());
    const sendable = this.pending.filter((entry) => !this.inKeepalive.has(entry.id));
    const requests = keepaliveRequests(sendable, budget, eventMaxBatchSizeInt);
    if (requests.length === 0) return;
    // Mark and claim before waiting for the token, so a teardown signal that fires in
    // the meantime neither re-sends these events nor counts their bytes as free.
    const outgoing = requests.map((batch) => {
      batch.forEach((entry) => {
        this.inFlight.add(entry.id);
        this.inKeepalive.add(entry.id);
      });
      const body = JSON.stringify(batch.map((entry) => entry.event));
      return { batch, body, releaseClaim: claimKeepaliveChars(body.length) };
    });
    let token: string | undefined;
    try {
      token = await getCurrentAuthToken();
    } catch {
      token = undefined;
    }
    const versionHeaders = appVersionHeaders();
    await Promise.all(
      outgoing.map(({ batch, body, releaseClaim }) =>
        this.sendKeepalive(batch, body, token, versionHeaders, releaseClaim),
      ),
    );
  }

  // Just before the user signs out, while the token still works: send everything
  // not yet accepted (queued, or waiting out a resend backoff), oldest first, and
  // mark the last batch as the sitting's end so the server ends it as signed out.
  // Whatever does not land is dropped at sign-out, as before (discardPending). With
  // nothing pending there is nothing to carry the mark, and the server ends the
  // sitting when it has been idle long enough.
  async flushBeforeSignOut(): Promise<void> {
    if (process.env.REACT_APP_SEND_EVENTS !== 'true') return;
    this.cutQueue();
    const sendable = this.pending.filter((entry) => !this.inFlight.has(entry.id));
    if (sendable.length === 0) return;
    const batches = chunk(sendable, eventMaxBatchSizeInt);
    const lastIndex = batches.length - 1;
    const generation = this.discardGeneration;
    for (const [index, batch] of batches.entries()) {
      // The sign-out went ahead without waiting for the rest: they are dropped, and
      // never sent under whichever account signs in next.
      if (this.discardGeneration !== generation) return;
      await this.postBatch(batch, index === lastIndex ? { [SESSION_END_HEADER]: SIGNED_OUT } : {});
    }
  }

  // On sign-out: drop every event not yet accepted - queued, waiting for a resend,
  // or in a send that has not answered (its answer then changes nothing) - so none
  // is sent later under whichever account signs in next.
  discardPending() {
    this.queue = [];
    this.pending = [];
    this.discardGeneration += 1;
    this.retainedLimitReported = false;
  }

  async dispatchEvents(callback?: () => void) {
    if (process.env.REACT_APP_SEND_EVENTS == 'true') {
      try {
        if (this.queue.length > 0 || this.pending.length > 0) {
          await this.clearQueue();
        }
        if (callback) {
          callback();
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      logger.debug('REACT_APP_SEND_EVENTS not set - not sending events.');
    }
  }
}

// singleton is important here
export const eventQueue = new EventQueue();
